import { TextField, Combobox, Select, Checkbox, FileUpload } from '@cinesend/ui-kit'
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import useStore from '../../hooks/use_store'
import useLogoUpload from '../../hooks/use_logo_upload'
import useUtilities from '../../hooks/use_utilities'
import AirlineSnapshotSizeInput from '../../components/airline_snapshot_size_input'

const Details = ({ airline }) => {
  const { data: languages } = useQuery(['/api/languages'])
  const { data: cycleFrequencies } = useQuery(['/api/cycle-frequencies'])
  const { data: cmsEnvironments } = useQuery(['/api/cms-environments'])
  const { assetTypes } = useUtilities()
  const { updateModel } = useStore()
  const [state, setState] = useState(airline)
  const updateState = (data) => setState({ ...state, ...data })
  const queryClient = useQueryClient()
  const saveModel = (data) => {
    let newState = {
      ...data,
    }

    if (newState.languages) {
      // ensure state object remains consistent with response structure
      newState.languages = languages.filter((lang) => data.languages.includes(lang.id))

      // if default language was removed from the list, also clear out the db field
      if (!data.languages.includes(state.default_language_id)) {
        newState.default_language_id = null
        data.default_language_id = null
      }
    }

    updateState(newState)
    updateModel.mutate(
      {
        endpoint: 'airlines',
        id: airline.id,
        data,
      },
      {
        onSuccess: () => queryClient.setQueryData(['airlines/', airline.id], { ...airline, ...data }),
      }
    )
  }
  const logoUpload = useLogoUpload({
    url: `${import.meta.env.VITE_API_URL}/api/airlines/upload-logo/${airline.id}`,
    onComplete: () => queryClient.fetchQuery(['airlines/', airline.id]).then((res) => updateState(res)),
  })
  const pluralAssetTypes = assetTypes?.map((opt) => ({ ...opt, label: opt.plural_label }))
  const mapLanguageToOption = (language) => ({ value: String(language.id), label: language.eng_description })

  return (
    <div className='grid grid-cols-2 gap-3'>
      <div className='space-y-4 mt-4'>
        <TextField
          label='Name'
          value={state?.name ?? ''}
          onChange={(e) => updateState({ name: e.target.value })}
          onBlur={() => saveModel({ name: state.name })}
        />

        <div className='grid grid-cols-2 gap-2'>
          <TextField
            label='Group code'
            value={state?.group_code ?? ''}
            onChange={(e) => updateState({ group_code: e.target.value })}
            onBlur={() => saveModel({ group_code: state.group_code })}
          />

          <Select
            fullWidth
            label='CMS Environment'
            options={cmsEnvironments ?? []}
            value={state.cms_environment ?? null}
            onValueChange={(value) => {
              saveModel({ cms_environment: value })
            }}
          />
        </div>

        <Combobox
          label='Asset Types'
          placeholder='Movies, series, etc.'
          options={pluralAssetTypes ?? []}
          multiple
          value={state?.asset_types ?? []}
          onValueChange={(selected) => {
            saveModel({ asset_types: selected })
          }}
        />

        <Select
          fullWidth
          label='Cycle Frequency'
          placeholder='(monthly, every 2-months, quarterly, etc.)'
          options={cycleFrequencies ?? []}
          value={state.cycle_frequency ?? null}
          onValueChange={(value) => {
            saveModel({ cycle_frequency: value })
          }}
        />

        <Combobox
          label='Portal Languages'
          placeholder={'Portal languages'}
          options={languages ? languages.map(mapLanguageToOption) : []}
          multiple
          value={state.languages?.map((language) => String(language.id)) ?? []}
          onValueChange={(ids) => {
            saveModel({ languages: ids.map(Number) })
          }}
        />
        <Select
          fullWidth
          label='Default Language'
          options={state.languages?.map(mapLanguageToOption) ?? []}
          value={airline.default_language_id ? String(airline.default_language_id) : null}
          onValueChange={(id) => {
            saveModel({ default_language_id: Number(id) })
          }}
        />
        <TextField
          label='Recall Period (Days)'
          value={state.recall_period ?? ''}
          type='number'
          onChange={(e) => updateState({ recall_period: e.target.value })}
          onBlur={() => saveModel({ recall_period: state.recall_period })}
        />
      </div>
      <div className='space-y-4 mt-4'>
        <FileUpload
          className='h-40'
          accept='.jpg,.jpeg,.png'
          message='Drop image here'
          buttonLabel='Upload Logo'
          imageUrl={state.logo_url}
          onFileSelected={logoUpload.upload}
          uploading={logoUpload.uploading}
          error={logoUpload.error}
        />

        <AirlineSnapshotSizeInput
          fieldName='max_total_snapshot_size'
          label='Max Snapshot Size - Total'
          value={state?.max_total_snapshot_size}
          saveModel={saveModel}
        />

        <AirlineSnapshotSizeInput
          fieldName='max_delta_snapshot_size'
          label='Max Snapshot Size - Delta'
          value={state?.max_delta_snapshot_size}
          saveModel={saveModel}
        />

        <AirlineSnapshotSizeInput
          fieldName='max_for_eis_size'
          label='Max "For EIS" Size'
          value={state?.max_for_eis_size}
          saveModel={saveModel}
        />
        <fieldset className='p-2 border rounded-lg flex items-center space-x-2'>
          <legend className='text-xs'>Ad / Warning Slate Settings</legend>
          <Checkbox
            className='shrink-0'
            text='Is Fast-Forwardable?'
            checked={!!state?.is_ad_fast_forwardable}
            onClick={() => {
              saveModel({ is_ad_fast_forwardable: !state?.is_ad_fast_forwardable })
            }}
          />
          <TextField
            label='Skippable After (seconds)'
            value={state?.ad_skippable_after_seconds ?? ''}
            onChange={(e) => updateState({ ad_skippable_after_seconds: e.target.value })}
            onBlur={() => saveModel({ ad_skippable_after_seconds: state.ad_skippable_after_seconds })}
          />
        </fieldset>
      </div>
    </div>
  )
}

export default Details
